/**
 * Police bitmap du jeu, dessinée en code : capitales 5 × 7 (accents français au-dessus), chiffres à chasse
 * fixe pour les compteurs. Elle sert aux scores, aux titres et aux comptes à rebours ; les textes courants
 * restent dans une sans lisible.
 *
 * Chaque glyphe est une liste de lignes ('#' allumé). Les lignes 0 et 1 portent les accents, 2 à 8 la
 * lettre, 9 la cédille et la virgule.
 */

const ROWS = 10;
const LETTER_TOP = 2;
const DESCENDER = 9;

/** Lettres et signes de 7 lignes (lignes 2 à 8). */
const BODY: Record<string, string> = {
  A: ".###. #...# #...# ##### #...# #...# #...#",
  B: "####. #...# #...# ####. #...# #...# ####.",
  C: ".###. #...# #.... #.... #.... #...# .###.",
  D: "####. #...# #...# #...# #...# #...# ####.",
  E: "##### #.... #.... ####. #.... #.... #####",
  F: "##### #.... #.... ####. #.... #.... #....",
  G: ".###. #...# #.... #.### #...# #...# .####",
  H: "#...# #...# #...# ##### #...# #...# #...#",
  I: "### .#. .#. .#. .#. .#. ###",
  J: "..### ...#. ...#. ...#. ...#. #..#. .##..",
  K: "#...# #..#. #.#.. ##... #.#.. #..#. #...#",
  L: "#.... #.... #.... #.... #.... #.... #####",
  M: "#...# ##.## #.#.# #.#.# #...# #...# #...#",
  N: "#...# #...# ##..# #.#.# #..## #...# #...#",
  O: ".###. #...# #...# #...# #...# #...# .###.",
  P: "####. #...# #...# ####. #.... #.... #....",
  Q: ".###. #...# #...# #...# #.#.# #..#. .##.#",
  R: "####. #...# #...# ####. #.#.. #..#. #...#",
  S: ".###. #...# #.... .###. ....# #...# .###.",
  T: "##### ..#.. ..#.. ..#.. ..#.. ..#.. ..#..",
  U: "#...# #...# #...# #...# #...# #...# .###.",
  V: "#...# #...# #...# #...# #...# .#.#. ..#..",
  W: "#...# #...# #...# #.#.# #.#.# #.#.# .#.#.",
  X: "#...# #...# .#.#. ..#.. .#.#. #...# #...#",
  Y: "#...# #...# .#.#. ..#.. ..#.. ..#.. ..#..",
  Z: "##### ....# ...#. ..#.. .#... #.... #####",
  "0": ".###. #...# #..## #.#.# ##..# #...# .###.",
  "1": "..#.. .##.. ..#.. ..#.. ..#.. ..#.. .###.",
  "2": ".###. #...# ....# ...#. ..#.. .#... #####",
  "3": "##### ...#. ..#.. ...#. ....# #...# .###.",
  "4": "...#. ..##. .#.#. #..#. ##### ...#. ...#.",
  "5": "##### #.... ####. ....# ....# #...# .###.",
  "6": "..##. .#... #.... ####. #...# #...# .###.",
  "7": "##### ....# ...#. ..#.. .#... .#... .#...",
  "8": ".###. #...# #...# .###. #...# #...# .###.",
  "9": ".###. #...# #...# .#### ....# ...#. .##..",
  ".": ". . . . . . #",
  ",": ". . . . . . #",
  ":": ". # . . . # .",
  ";": ". # . . . # .",
  "!": "# # # # # . #",
  "?": ".###. #...# ....# ...#. ..#.. ..... ..#..",
  "'": "# # . . . . .",
  "-": "... ... ... ### ... ... ...",
  "+": "..... ..#.. ..#.. ##### ..#.. ..#.. .....",
  "/": "....# ....# ...#. ..#.. .#... #.... #....",
  "%": "##..# ##..# ...#. ..#.. .#... #..## #..##",
  "×": "..... #...# .#.#. ..#.. .#.#. #...# .....",
  "(": ".# #. #. #. #. #. .#",
  ")": "#. .# .# .# .# .# #.",
  "#": ".#.#. ##### .#.#. .#.#. .#.#. ##### .#.#.",
  "·": ". . . # . . .",
  "…": "..... ..... ..... ..... ..... ..... #.#.#",
  "<": "...# ..#. .#.. #... .#.. ..#. ...#",
  ">": "#... .#.. ..#. ...# ..#. .#.. #...",
  "=": "..... ..... ##### ..... ##### ..... .....",
};

/** Accents (lignes 0 et 1, sur une largeur de 5). */
const MARKS: Record<string, [string, string]> = {
  acute: ["...#.", "..#.."],
  grave: [".#...", "..#.."],
  circumflex: ["..#..", ".#.#."],
  diaeresis: [".....", ".#.#."],
};

/** Lettres accentuées : la lettre, son accent (ou la cédille). */
const ACCENTED: Record<string, { base: string; mark?: keyof typeof MARKS; cedilla?: boolean }> = {
  É: { base: "E", mark: "acute" },
  È: { base: "E", mark: "grave" },
  Ê: { base: "E", mark: "circumflex" },
  Ë: { base: "E", mark: "diaeresis" },
  À: { base: "A", mark: "grave" },
  Â: { base: "A", mark: "circumflex" },
  Î: { base: "I", mark: "circumflex" },
  Ï: { base: "I", mark: "diaeresis" },
  Ô: { base: "O", mark: "circumflex" },
  Ù: { base: "U", mark: "grave" },
  Û: { base: "U", mark: "circumflex" },
  Ü: { base: "U", mark: "diaeresis" },
  Ç: { base: "C", cedilla: true },
};

/** Les chiffres gardent leur chasse : un compteur ne danse pas quand il défile. */
const FIXED_WIDTH = new Set("0123456789");
const SPACE_WIDTH = 3;
const LETTER_SPACING = 1;

export interface Glyph {
  width: number;
  /** Lignes 0 à 9, chacune une chaîne de `width` caractères. */
  rows: string[];
}

function parseBody(source: string): string[] {
  return source.split(" ");
}

/** Retire les colonnes vides à gauche et à droite (chasse proportionnelle des lettres et signes). */
function trim(rows: string[], keepWidth: boolean): string[] {
  if (keepWidth) return rows;
  const width = Math.max(...rows.map((row) => row.length));
  let first = width;
  let last = -1;
  for (const row of rows) {
    for (let x = 0; x < row.length; x++) {
      if (row[x] !== "#") continue;
      first = Math.min(first, x);
      last = Math.max(last, x);
    }
  }
  if (last < 0) return rows;
  return rows.map((row) => row.padEnd(width, ".").slice(first, last + 1));
}

const cache = new Map<string, Glyph>();

function buildGlyph(char: string): Glyph | null {
  const accented = ACCENTED[char];
  const baseChar = accented ? accented.base : char;
  const body = BODY[baseChar];
  if (!body) return null;
  const letter = parseBody(body);
  const bodyWidth = Math.max(...letter.map((row) => row.length));
  const rows: string[] = Array.from({ length: ROWS }, () => ".".repeat(bodyWidth));
  letter.forEach((row, index) => {
    rows[LETTER_TOP + index] = row.padEnd(bodyWidth, ".");
  });
  if (accented?.mark) {
    const [top, bottom] = MARKS[accented.mark] ?? [".....", "....."];
    // L'accent se centre sur la lettre (le I est plus étroit).
    const offset = Math.floor((bodyWidth - 5) / 2);
    const place = (mark: string) =>
      offset >= 0 ? (".".repeat(offset) + mark).slice(0, bodyWidth).padEnd(bodyWidth, ".") : mark.slice(-offset, -offset + bodyWidth);
    rows[0] = place(top);
    rows[1] = place(bottom);
  }
  if (accented?.cedilla) rows[DESCENDER] = "..#..".slice(0, bodyWidth);
  // La virgule et le point-virgule descendent d'une ligne.
  if (char === "," || char === ";") rows[DESCENDER] = "#";
  const trimmed = trim(rows, FIXED_WIDTH.has(char));
  return { width: Math.max(...trimmed.map((row) => row.length)), rows: trimmed };
}

/** Glyphe d'un caractère (en capitale), ou `null` s'il n'existe pas dans la police. */
export function glyph(char: string): Glyph | null {
  const upper = char.toLocaleUpperCase("fr");
  let found = cache.get(upper);
  if (found === undefined) {
    const built = buildGlyph(upper);
    if (!built) return null;
    found = built;
    cache.set(upper, found);
  }
  return found;
}

/** Remplace ce que la police ne connaît pas (Œ, espaces fines…) par un équivalent. */
function normalize(text: string): string {
  return text
    .replace(/[   ]/g, " ")
    .replace(/œ/gi, "OE")
    .replace(/æ/gi, "AE")
    .replace(/[’‘]/g, "'")
    .replace(/[–—−]/g, "-");
}

/** Une ligne de texte posée en pixels : rangées de pixels allumés, en runs horizontaux. */
export interface TextBitmap {
  width: number;
  height: number;
  /** [x, y, longueur] de chaque suite de pixels allumés. */
  runs: [number, number, number][];
}

/**
 * Pose une ligne de texte. La hauteur se resserre : sans accent, pas de lignes du haut ; sans cédille ni
 * virgule, pas de ligne du bas.
 */
export function layoutText(text: string): TextBitmap {
  const chars = [...normalize(text)];
  const glyphs = chars.map((char) => (char === " " ? null : glyph(char)));
  const hasAccent = chars.some((char) => ACCENTED[char.toLocaleUpperCase("fr")]?.mark);
  const hasDescender = chars.some((char) => char === "," || char === ";" || char.toLocaleUpperCase("fr") === "Ç");
  const top = hasAccent ? 0 : LETTER_TOP;
  const bottom = hasDescender ? ROWS : DESCENDER;
  const runs: [number, number, number][] = [];
  let cursor = 0;
  chars.forEach((char, index) => {
    const found = glyphs[index];
    if (!found) {
      cursor += char === " " ? SPACE_WIDTH : 0;
      return;
    }
    for (let y = top; y < bottom; y++) {
      const row = found.rows[y] ?? "";
      let x = 0;
      while (x < row.length) {
        if (row[x] !== "#") {
          x++;
          continue;
        }
        let end = x;
        while (end < row.length && row[end] === "#") end++;
        runs.push([cursor + x, y - top, end - x]);
        x = end;
      }
    }
    cursor += found.width + LETTER_SPACING;
  });
  const width = Math.max(1, cursor - LETTER_SPACING);
  return { width, height: bottom - top, runs };
}
